import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CheckInType } from '../../model/CheckInType';
import { CheckInService } from '../../check-ins/services/check-ins.service';
import { CommonService } from '../../common/services/common.service';
import { IUser } from '../../model/interfaces/user';
import { PublicCheckInService } from '../public-check-in/services/public-check-in.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationsService } from '@mt-ng2/notifications-module';
import { IDelivery } from '../../model/interfaces/delivery';
import { PublicCheckInDTO } from '../../model/interfaces/custom/public-check-in-dto';
import { IOrderPartial } from '../../model/partials/order-partial';
import { ICheckIn } from '../../model/interfaces/check-in';
import { DeliveryService } from '../../deliveries/services/delivery.service';

@Component({
    selector: 'app-driver-delivering',
    templateUrl: './driver-delivering.component.html',
    styleUrls: ['./driver-delivering.component.css'],
    standalone: false
})
export class DriverDeliveringComponent implements OnInit {
    driver: IUser;
    editDriverForm: FormGroup;
    delivery: IDelivery;
    checkIn: ICheckIn;
    checkedIn = false;
    doubleClickIsDisabled = false;

    constructor(
        private router: Router, 
        private publicCheckInService: PublicCheckInService,
        private checkInService: CheckInService,
        private formBuilder: FormBuilder, 
        private commonService: CommonService,
        private notificationsService: NotificationsService,
        private deliveryService: DeliveryService
    ) { }

    ngOnInit(): void {
        this.publicCheckInService.checkedInDriver$.subscribe((driver) => {
            this.driver = driver;
            if (!driver) {
                void this.router.navigate(['public/driver-check-in-info']);
                return;
            }

            this.publicCheckInService.driverCreatedCheckIn$.subscribe((checkIn) => {
                this.checkIn = checkIn;
                if (this.checkIn?.Id > 0) {
                    this.checkDeliveryBalance(this.checkIn.Id);
                    return;
                }
                this.editDriverForm = this.formBuilder.group({
                    firstName: [this.driver.FirstName, [Validators.required, this.commonService.firstAndLastNameValidator()]],
                    lastName: [this.driver.LastName, [Validators.required, this.commonService.firstAndLastNameValidator()]],
                    licensePlateNumber: ['', [Validators.required, this.commonService.checkForAlphaNumericCharacters()]],
                    driverLicenseNumber: [''],
                    containerNumber: ['', [Validators.required, this.commonService.checkForAlphaNumericCharacters()]],
                });
            });
        });
    }

    onSubmit(): void {
        const orderNumber = this.editDriverForm.get('containerNumber')?.value as string;
        this.publicCheckInService.getValidDeliveryByContainerNumber(orderNumber).subscribe({
            next: (delivery: IDelivery) => this.handleDeliveryResponse(delivery, orderNumber),
            error: (error: HttpErrorResponse) => this.commonService.handleErrorResponse(error),
        });
    }

    private handleDeliveryResponse(delivery: IDelivery, orderNumber: string): void {
        this.delivery = delivery;
        if (!this.delivery) {
            this.notificationsService.error('Invalid Container # / Trailer # number. Please try again.');
            return;
        }

        this.checkIn = delivery.CheckIns.find(
            (checkIn) => checkIn.CheckInUserId === this.driver.Id
        );

        if (this.checkIn?.Id > 0) {
            this.checkDeliveryBalance(this.checkIn.Id);
            return;
        }

        const order = this.createOrder(orderNumber);
        const publicCheckInDTO = this.createPublicCheckInDTO(delivery, order);

        this.publicCheckInService.submitPublicCheckIn(publicCheckInDTO).subscribe({
            next: (checkInId: number) => this.handleCheckInSubmission(checkInId),
            error: (error: HttpErrorResponse) => this.commonService.handleErrorResponse(error),
        });
    }

    private handleCheckInSubmission(checkInId: number): void {
        this.publicCheckInService.sendCheckInNotifications(checkInId).subscribe({
            next: (notified: boolean) => this.handleNotificationResponse(checkInId, notified),
            error: (error: HttpErrorResponse) => this.commonService.handleErrorResponse(error),
        });
    }

    private handleNotificationResponse(checkInId: number, notified: boolean): void {
        const checkInLog = {
            CheckInId: checkInId,
            DateCreatedUtc: new Date(),
            Id: 0,
            LogMessage: '[PUBLIC DELIVERY CHECK-IN] driver successfully checked in, but notifications failed to send.',
        };

        if (notified) {
            checkInLog.LogMessage = '[PUBLIC DELIVERY CHECK-IN] driver successfully checked in. Notifications sent successfully.';
        }

        this.checkInService.createCheckInLog(checkInLog).subscribe({
            next: (checkIn) => {
                this.checkIn = checkIn;
                this.checkDeliveryBalance(checkIn.Id);
            },
            error: (error: HttpErrorResponse) => this.commonService.handleErrorResponse(error),
        });
    }

    private checkDeliveryBalance(checkInId: number): void {
        this.publicCheckInService.getRemainingBalanceForDelivery(checkInId).subscribe({
            next: (remainingBalance: number) => {
                if (remainingBalance > 0) {
                    // Check if carrier has credit available and apply it first
                    if (this.delivery.CarrierId && this.delivery.CarrierId > 0) {
                        this.deliveryService.checkCarrierCreditEligibility(this.delivery.Id).subscribe({
                            next: (creditEligibility) => {
                                if (creditEligibility.canUseCredit && creditEligibility.availableCredit >= remainingBalance) {
                                    // Carrier has sufficient credit, apply it instead of routing to payment
                                    this.applyCarrierCredit();
                                } else {
                                    // Not enough credit or no credit available, route to payment page
                                    this.delivery.CheckIns = [this.checkIn];
                                    this.publicCheckInService.checkedInDelivery$.next(this.delivery);
                                    void this.router.navigate(['public/driver-check-in-delivery-payment']);
                                }
                            },
                            error: () => {
                                // Error checking credit, proceed to payment page
                                this.delivery.CheckIns = [this.checkIn];
                                this.publicCheckInService.checkedInDelivery$.next(this.delivery);
                                void this.router.navigate(['public/driver-check-in-delivery-payment']);
                            }
                        });
                    } else {
                        // No carrier assigned or invalid carrier ID, route to payment page
                        this.delivery.CheckIns = [this.checkIn];
                        this.publicCheckInService.checkedInDelivery$.next(this.delivery);
                        void this.router.navigate(['public/driver-check-in-delivery-payment']);
                    }
                } else {
                    this.checkedIn = true;
                    // Reload the page after 5 seconds once the driver is checked in
                    // This will redirect to the driver portal login page
                    setTimeout(() => {
                        location.reload();
                        return;
                    }, 5000);
                }
            },
            error: (error: HttpErrorResponse) => this.commonService.handleErrorResponse(error),
        });
    }

    private applyCarrierCredit(): void {
        this.deliveryService.processDeliveryWithCredit(this.delivery.Id).subscribe({
            next: (result) => {
                if (result.Success) {
                    this.notificationsService.success(`${this.driver.FirstName} ${this.driver.LastName}, your payment has been successfully applied to carrier credit.`);
                    this.checkedIn = true;
                    // Reload the page after 5 seconds once the driver is checked in
                    // This will redirect to the driver portal login page
                    
                    // send delivery detail to FTP and navigate to checked-in page
                    this.publicCheckInService.exportDeliveryDataViaFtp(this.checkIn.Id).subscribe({
                        next: () => {
                            // do nothing, just ensure FTP export happens before page reload
                        },
                    });

                    setTimeout(() => {
                        location.reload();
                        return;
                    }, 5000);
                } else {
                    this.notificationsService.error('Failed to apply carrier credit. Please try the payment page.');
                    // Route to payment page as fallback
                    this.delivery.CheckIns = [this.checkIn];
                    this.publicCheckInService.checkedInDelivery$.next(this.delivery);
                    void this.router.navigate(['public/driver-check-in-delivery-payment']);
                }
            },
            error: () => {
                this.notificationsService.error('Error applying carrier credit. Please try the payment page.');
                // Route to payment page as fallback
                this.delivery.CheckIns = [this.checkIn];
                this.publicCheckInService.checkedInDelivery$.next(this.delivery);
                void this.router.navigate(['public/driver-check-in-delivery-payment']);
            }
        });
    }
    
    private createPublicCheckInDTO(delivery: IDelivery, order: IOrderPartial): PublicCheckInDTO {
        return {
            firstName: this.editDriverForm.get('firstName')?.value,
            lastName: this.editDriverForm.get('lastName')?.value,
            licensePlate: this.editDriverForm.get('licensePlateNumber')?.value,
            driversLicense: this.editDriverForm.get('driverLicenseNumber')?.value,
            carrierId: this.driver.CarrierId,
            phone: this.driver.Phone,
            checkInTypeId: CheckInType.PICKUP,
            validOrders: [order],
            deliveryId: delivery.Id,
            driverId: this.driver.Id,
        };
    }
    
    private createOrder(orderNumber: string): IOrderPartial {
        const order = this.publicCheckInService.getEmptyOrder();
        order.OrderNumber = orderNumber;
        return order;
    }
}
